import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { garantirUsuario } from '@/lib/usuario';
import { guardarDestino } from '@/lib/destino';
import { reduzirImagem } from '@/lib/imagem';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { mensagemSenhaRepetida } from '@/lib/senhaFilaErro';
import { mostrarToast } from '@/lib/formularioGuiado';
import { EMAIL_REGEX, validarCPF } from '@/pages/inscricao/utils';
import type { Participacao } from '@/types/evento-publico';
import CampoFoto from './components/CampoFoto';
import CampoPergunta from './components/CampoPergunta';
import ProtecaoPergunta from './components/ProtecaoPergunta';
import TextoRico from '@/components/base/TextoRico';
import { ehPerguntaDoTermo, marcouFilhos } from '@/lib/filhosFicha';
import { chaveDaSecao, perguntasSeguras, rascunhoSeguro } from './sanitizar';
import {
  alturaEmCm,
  normalizarAltura,
  normalizarPeso,
  normalizarSenha,
  numeroBr,
  perguntaVisivel,
} from './utils';
import type { LinhaLista, PerguntaFicha, ValorResposta } from './types';

interface BlocoFicha {
  secao: string | null;
  perguntas: PerguntaFicha[];
  /** Este bloco tinha alguma pergunta do termo (escondida nesta tela). */
  tinhaTermo?: boolean;
}

// Perguntas seguidas com a mesma seção formam um bloco.
// Perguntas sem seção (a ficha do campista) formam um bloco único.
// A ordem vem da coluna `ordem`, nunca do id ou da ordem de retorno do banco.
// "Família" e "Família:" contam como a MESMA seção — senão a pergunta
// condicional cai num bloco sozinho e a tela fica em branco.
function agruparBlocos(perguntas: PerguntaFicha[]): BlocoFicha[] {
  const blocos: BlocoFicha[] = [];
  let chaveAtual: string | null | undefined;
  let temBloco = false;

  const ordenadas = [...perguntas].sort((a, b) => a.ordem - b.ordem);

  ordenadas.forEach((p) => {
    const chave = chaveDaSecao(p.secao);
    const ultimo = blocos[blocos.length - 1];

    if (temBloco && chaveAtual === chave && ultimo) {
      ultimo.perguntas.push(p);
      return;
    }

    blocos.push({ secao: p.secao, perguntas: [p] });
    chaveAtual = chave;
    temBloco = true;
  });

  return blocos;
}

// Considera o rascunho "com resposta" quando ao menos um campo foi preenchido.
function temRespostaNoDraft(dados: Record<string, ValorResposta>): boolean {
  return Object.values(dados).some((v) => {
    if (v == null) return false;
    if (typeof v === 'string') return v.trim() !== '';
    if (Array.isArray(v)) return v.length > 0;
    return true;
  });
}

export default function FichaPage() {
  const { modalidadeId } = useParams<{ modalidadeId: string }>();
  const [searchParams] = useSearchParams();
  const participanteParam = searchParams.get('participante');
  const opcaoParam = searchParams.get('opcao');
  const navigate = useNavigate();
  const { user, pronto } = useAuth();

  const [uid, setUid] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const [partes, setPartes] = useState<Participacao[]>([]);
  const [eventoNome, setEventoNome] = useState('');
  const [formDescricao, setFormDescricao] = useState<string | null>(null);
  const [perguntas, setPerguntas] = useState<PerguntaFicha[]>([]);

  const [respostas, setRespostas] = useState<Record<string, ValorResposta | undefined>>({});
  const [fotos, setFotos] = useState<Record<string, File | null>>({});
  const [fotoCadastro, setFotoCadastro] = useState<string | null>(null);
  // Esta ficha é da própria pessoa logada? (participantes.usuario_id = uid)
  const [ehMinhaFicha, setEhMinhaFicha] = useState(false);

  const [blocoAtual, setBlocoAtual] = useState(0);
  const [salvando, setSalvando] = useState(false);
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const [erros, setErros] = useState<Record<string, string>>({});
  const [enviandoFoto, setEnviandoFoto] = useState<Record<string, boolean>>({});
  // Foto que não deu para enviar: a pessoa segue sem ela, avisada no campo.
  const [fotoFalhou, setFotoFalhou] = useState<Record<string, boolean>>({});
  const [rascunhoRecuperado, setRascunhoRecuperado] = useState(false);

  const carregadoRef = useRef(false);
  const refErro = useRef<HTMLDivElement>(null);
  // Envios de foto em andamento (a ficha nunca espera por eles, só o Concluir).
  const pendentesRef = useRef<Set<Promise<unknown>>>(new Set());
  const enviandoCountRef = useRef(0);
  // Último caminho salvo de cada foto — vale mais que o estado do React
  // quando o envio termina no exato instante do "Concluir".
  const caminhosFotoRef = useRef<Record<string, string | null>>({});
  const arquivoFotoRef = useRef<Record<string, File | null>>({});

  const participanteId = participanteParam ? Number(participanteParam) : null;
  const grupoId = opcaoParam ? Number(opcaoParam) : null;

  const chaveRascunho = useMemo(
    () => `ficha:${modalidadeId}:${participanteId}`,
    [modalidadeId, participanteId],
  );

  useEffect(() => {
    let ativo = true;
    // Vira true só depois que o rascunho foi lido — nunca no return de auth,
    // senão o efeito de salvar apagaria o rascunho antes de lê-lo.
    let carregou = false;

    async function carregar() {
      setLoading(true);
      setErro(null);
      try {
        if (!pronto) return;
        if (!user) {
          guardarDestino();
          navigate(
            `/entrar?voltar=${encodeURIComponent(
              `/inscricao/${modalidadeId}/ficha?participante=${participanteParam ?? ''}${
                opcaoParam ? `&opcao=${opcaoParam}` : ''
              }`,
            )}`,
            { replace: true },
          );
          return;
        }
        if (ativo) setUid(user.id);
        await garantirUsuario();

        // Sem saber de quem é a ficha, não há como continuar.
        if (!participanteId || Number.isNaN(participanteId)) {
          navigate(`/inscricao/${modalidadeId}${opcaoParam ? `?opcao=${opcaoParam}` : ''}`, {
            replace: true,
          });
          return;
        }

        // Foto que a pessoa já tem no cadastro (para reusar na ficha).
        // Se a ficha for da própria pessoa logada, a foto nova vira a foto do perfil.
        const { data: participanteData } = await supabase
          .from('participantes')
          .select('foto_url, usuario_id')
          .eq('id', participanteId)
          .maybeSingle();
        if (ativo) {
          const linha = participanteData as
            | { foto_url: string | null; usuario_id: string | null }
            | null;
          setFotoCadastro(linha?.foto_url ?? null);
          setEhMinhaFicha(!!linha?.usuario_id && linha.usuario_id === user.id);
        }

        const { data: partesData, error: errPartes } = await supabase
          .from('vw_participacoes')
          .select('*')
          .eq('modalidade_id', Number(modalidadeId));
        if (errPartes) throw errPartes;

        const lista = (partesData as Participacao[]) || [];
        if (lista.length === 0) {
          if (ativo) setErro('Não encontramos esta forma de participar.');
          return;
        }
        if (ativo) setPartes(lista);

        const eventoId = lista[0].evento_id;
        const { data: ev } = await supabase
          .from('eventos')
          .select('nome')
          .eq('id', eventoId)
          .maybeSingle();
        if (ativo) setEventoNome(ev?.nome || '');

        const { data: formData } = await supabase
          .from('formularios')
          .select('id, nome, descricao')
          .eq('modalidade_id', Number(modalidadeId))
          .eq('ativo', true)
          .maybeSingle();

        if (!formData) {
          // Sem ficha: essa modalidade não tem perguntas. Grava a inscrição direto.
          const { data: insc, error: errIns } = await supabase
            .from('inscricoes')
            .insert({
              evento_id: lista[0].evento_id,
              modalidade_id: Number(modalidadeId),
              opcao_id: grupoId ?? null,
              participante_id: participanteId,
              responsavel_id: user.id,
            })
            .select('codigo')
            .single();
          if (errIns) {
            if (ativo) setErro(mensagemAmigavel(errIns));
            return;
          }
          if (ativo) {
            navigate(`/minha-inscricao/${(insc as { codigo: string }).codigo}?nova=1`, {
              replace: true,
            });
          }
          return;
        }

        if (ativo) setFormDescricao((formData as { descricao: string | null }).descricao);

        const { data: perguntasData, error: errPerguntas } = await supabase
          .from('perguntas')
          .select('*')
          .eq('formulario_id', (formData as { id: number }).id)
          .order('ordem');
        if (errPerguntas) throw errPerguntas;

        // Perguntas que não dá para renderizar, condições órfãs e tipos
        // desconhecidos são descartados/ignorados aqui — a ficha nunca quebra.
        const listaPerguntas: PerguntaFicha[] = perguntasSeguras(perguntasData);

        // Recupera o rascunho, se houver (expira em 24 h).
        const salvo = localStorage.getItem(chaveRascunho);
        if (salvo) {
          try {
            const parsed = JSON.parse(salvo) as {
              dados?: Record<string, ValorResposta>;
              salvoEm?: number;
            };
            const expirou =
              typeof parsed?.salvoEm !== 'number' ||
              Date.now() - parsed.salvoEm > 24 * 60 * 60 * 1000;
            if (expirou) {
              localStorage.removeItem(chaveRascunho);
            } else if (parsed.dados && ativo) {
              // Só entra o que ainda existe e no formato certo: pergunta
              // apagada, tipo trocado ou rascunho antigo com outro formato
              // são ignorados em vez de quebrar a tela.
              const dados = rascunhoSeguro(parsed.dados, listaPerguntas);
              setRespostas(dados);
              // Só avisa "Recuperamos…" se havia algo de fato respondido.
              if (temRespostaNoDraft(dados)) setRascunhoRecuperado(true);
            }
          } catch {
            // rascunho corrompido: ignora
          }
        }

        if (ativo) setPerguntas(listaPerguntas);
        // A ficha só conta como "carregada" depois de ler o rascunho.
        carregou = true;
      } catch (e) {
        if (ativo) {
          setErro(mensagemAmigavel(e, 'Não foi possível carregar a ficha. Tente novamente.'));
        }
      } finally {
        if (ativo) {
          if (carregou) carregadoRef.current = true;
          setLoading(false);
        }
      }
    }

    carregar();
    return () => {
      ativo = false;
    };
  }, [modalidadeId, participanteParam, opcaoParam, navigate, chaveRascunho, pronto, user]);

  // O termo das crianças só é assinado pelo link dos pais.
  const perguntasSemTermo = useMemo(
    () => perguntas.filter((p) => !ehPerguntaDoTermo(p.chave)),
    [perguntas],
  );

  const blocosCompletos = useMemo(() => agruparBlocos(perguntas), [perguntas]);
  const blocos = useMemo<BlocoFicha[]>(
    () =>
      blocosCompletos.map((b) => ({
        secao: b.secao,
        perguntas: b.perguntas.filter((p) => !ehPerguntaDoTermo(p.chave)),
        tinhaTermo: b.perguntas.some((p) => ehPerguntaDoTermo(p.chave)),
      })),
    [blocosCompletos],
  );

  const levaFilhos = useMemo(
    () =>
      marcouFilhos(
        perguntas.map((p) => ({ chave: p.chave, valor: respostas[String(p.id)] })),
      ),
    [perguntas, respostas],
  );

  const perguntasVisiveis = useMemo(
    () => perguntasSemTermo.filter((p) => perguntaVisivel(p, respostas)),
    [perguntasSemTermo, respostas],
  );

  // O que aparece de verdade em cada bloco, com as respostas atuais.
  const visiveisPorBloco = useMemo(
    () => blocos.map((b) => b.perguntas.filter((p) => perguntaVisivel(p, respostas))),
    [blocos, respostas],
  );

  // Blocos que têm pelo menos uma pergunta visível. Um bloco só com perguntas
  // escondidas nunca é mostrado — era isso que deixava a tela em branco.
  const indicesVisiveis = useMemo(
    () =>
      visiveisPorBloco.reduce<number[]>((acc, lista, i) => {
        if (lista.length > 0) acc.push(i);
        return acc;
      }, []),
    [visiveisPorBloco],
  );

  // Se o bloco atual ficou vazio, pula para o primeiro que tem pergunta.
  useEffect(() => {
    if (indicesVisiveis.length === 0) return;
    if (!indicesVisiveis.includes(blocoAtual)) setBlocoAtual(indicesVisiveis[0]);
  }, [indicesVisiveis, blocoAtual]);

  const posicaoNoBloco = indicesVisiveis.indexOf(blocoAtual);

  // Guarda o rascunho a cada resposta (depois do carregamento inicial), com carimbo de tempo.
  useEffect(() => {
    if (!carregadoRef.current) return;
    if (!participanteId || !modalidadeId) return;
    const dados: Record<string, ValorResposta> = {};
    Object.entries(respostas).forEach(([k, v]) => {
      if (v !== undefined) dados[k] = v;
    });
    // Nunca sobrescreve um rascunho existente com um objeto vazio.
    if (Object.keys(dados).length === 0) {
      try {
        if (localStorage.getItem(chaveRascunho)) return;
      } catch {
        // armazenamento indisponível: segue
      }
    }
    try {
      localStorage.setItem(chaveRascunho, JSON.stringify({ dados, salvoEm: Date.now() }));
    } catch {
      // armazenamento cheio ou indisponível: ignora
    }
  }, [respostas, chaveRascunho, participanteId, modalidadeId]);

  // Ao sair da conta, apaga o rascunho.
  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        try {
          localStorage.removeItem(chaveRascunho);
        } catch {
          // ignora
        }
      }
    });
    return () => {
      sub.subscription.unsubscribe();
    };
  }, [chaveRascunho]);

  const exibirBlocos = indicesVisiveis.length > 1;
  const progresso =
    posicaoNoBloco >= 0 && indicesVisiveis.length > 0
      ? ((posicaoNoBloco + 1) / indicesVisiveis.length) * 100
      : 0;

  const grupoNome = useMemo(() => {
    if (grupoId == null) return null;
    return partes.find((p) => p.opcao_id === grupoId)?.grupo ?? null;
  }, [partes, grupoId]);

  const subtitulo = useMemo(
    () => [partes[0]?.modalidade, grupoNome].filter(Boolean).join(' · '),
    [partes, grupoNome],
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [blocoAtual]);

  useEffect(() => {
    if (erroGeral) refErro.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [erroGeral]);

  function aoResponder(id: number, valor: ValorResposta) {
    setRespostas((prev) => {
      const novo = { ...prev, [String(id)]: valor };
      // Se a resposta de cima mudou, apaga o que as perguntas dependentes tinham.
      perguntas.forEach((p) => {
        if (p.condicaoPerguntaId === id) delete novo[String(p.id)];
      });
      return novo;
    });
    setErros((e) => ({ ...e, [String(id)]: '' }));
  }

  function aoSelecionarFoto(id: number, arquivo: File | null) {
    const chave = String(id);
    setFotos((p) => ({ ...p, [chave]: arquivo }));
    arquivoFotoRef.current[chave] = arquivo;
    setErros((p) => ({ ...p, [chave]: '' }));
    setFotoFalhou((p) => ({ ...p, [chave]: false }));

    if (!arquivo) {
      caminhosFotoRef.current[chave] = null;
      setRespostas((r) => ({ ...r, [chave]: undefined }));
      return;
    }

    // O envio roda em segundo plano: a miniatura aparece na hora e a pessoa
    // continua preenchendo e avançando normalmente.
    setEnviandoFoto((p) => ({ ...p, [chave]: true }));
    const tarefa = enviarFoto(chave, arquivo);
    pendentesRef.current.add(tarefa);
    tarefa.finally(() => {
      pendentesRef.current.delete(tarefa);
    });
  }

  async function enviarFoto(chave: string, arquivo: File) {
    enviandoCountRef.current += 1;
    try {
      let corpo: Blob;
      try {
        // Reduz no próprio celular: 800 px no lado maior, JPEG 0.7.
        corpo = await reduzirImagem(arquivo, 800, 0.7, 'image/jpeg');
      } catch {
        // HEIC do iPhone, ou formato que o navegador não abre:
        // manda o arquivo original, que o servidor aceita.
        corpo = arquivo;
      }

      const caminho = `${uid}/${crypto.randomUUID()}.jpg`;
      const { error } = await supabase.storage
        .from('fichas')
        .upload(caminho, corpo, { contentType: corpo.type || 'image/jpeg' });

      // A pessoa trocou/removeu a foto enquanto esta subia: descarta o resultado.
      if (arquivoFotoRef.current[chave] !== arquivo) return;

      if (error) {
        caminhosFotoRef.current[chave] = null;
        setFotoFalhou((p) => ({ ...p, [chave]: true }));
        setErros((p) => ({
          ...p,
          [chave]: 'Não deu para enviar a foto. Tente de novo ou continue sem foto.',
        }));
        return;
      }

      // O caminho vira uma RESPOSTA normal — e por isso entra no rascunho.
      caminhosFotoRef.current[chave] = caminho;
      setRespostas((r) => ({ ...r, [chave]: caminho }));
    } catch {
      if (arquivoFotoRef.current[chave] !== arquivo) return;
      caminhosFotoRef.current[chave] = null;
      setFotoFalhou((p) => ({ ...p, [chave]: true }));
      setErros((p) => ({
        ...p,
        [chave]: 'Não deu para enviar a foto. Tente de novo ou continue sem foto.',
      }));
    } finally {
      enviandoCountRef.current = Math.max(0, enviandoCountRef.current - 1);
      setEnviandoFoto((p) => ({ ...p, [chave]: false }));
    }
  }

  // Espera as fotos que ainda estão subindo, no máximo 15 segundos.
  // Retorna true se todas terminaram nesse tempo.
  async function esperarFotos(limiteMs = 15000): Promise<boolean> {
    if (enviandoCountRef.current === 0) return true;
    const pendentes = Array.from(pendentesRef.current);
    if (pendentes.length === 0) return enviandoCountRef.current === 0;
    await Promise.race([
      Promise.allSettled(pendentes),
      new Promise((resolve) => setTimeout(resolve, limiteMs)),
    ]);
    return enviandoCountRef.current === 0;
  }

  function comecarDoZero() {
    try {
      localStorage.removeItem(chaveRascunho);
    } catch {
      // ignora
    }
    setRespostas({});
    setFotos({});
    setErros({});
    setRascunhoRecuperado(false);
  }

  function avisoNaoQuer(p: PerguntaFicha): string | null {
    if (p.chave !== 'nao_quer' || p.tipo !== 'multipla_escolha') return null;
    const v = respostas[String(p.id)];
    if (!Array.isArray(v) || v.length <= 3) return null;
    return `Você marcou ${v.length}. A partir de 4 a organização desconsidera esta resposta.`;
  }

  function validarPerguntas(lista: PerguntaFicha[]): Record<string, string> {
    const novosErros: Record<string, string> = {};
    lista.forEach((p) => {
      if (!p.obrigatoria) return;
      const chave = String(p.id);
      if (p.tipo === 'foto') {
        // Foto subindo ou que falhou nunca trava a ficha: o envio é tratado
        // no Concluir (espera até 15 s) e, se não der, segue sem a foto.
        if (enviandoFoto[chave] || enviandoCountRef.current > 0 || fotoFalhou[chave]) return;
        if (!respostas[chave] && !caminhosFotoRef.current[chave] && !fotoCadastro) {
          novosErros[chave] = 'É preciso enviar uma foto para concluir a inscrição.';
        }
        return;
      }
      const v = respostas[chave];
      if (p.tipo === 'lista') {
        const linhas = (Array.isArray(v) ? v : []) as LinhaLista[];
        const temLinha = linhas.some((l) =>
          Object.values(l).some((col) => String(col ?? '').trim() !== ''),
        );
        if (!temLinha) novosErros[chave] = 'Adicione pelo menos um.';
        return;
      }
      if (Array.isArray(v)) {
        if (v.length === 0) novosErros[chave] = 'Escolha pelo menos uma opção.';
        return;
      }
      if (!String(v ?? '').trim()) novosErros[chave] = 'Esta pergunta é obrigatória.';
    });

    // Validação de formato, só quando preenchido.
    lista.forEach((p) => {
      const chave = String(p.id);
      const v = respostas[chave];
      if (typeof v !== 'string' || !v.trim()) return;
      if (p.tipo === 'cpf' && !validarCPF(v)) novosErros[chave] = 'Verifique o CPF digitado.';
      if (p.tipo === 'email' && !EMAIL_REGEX.test(v)) {
        novosErros[chave] = 'Digite um e-mail válido, como maria@gmail.com';
      }
      if (p.tipo === 'telefone') {
        const digitos = v.replace(/\D/g, '');
        if (digitos.length !== 10 && digitos.length !== 11) {
          novosErros[chave] = 'Digite o telefone com DDD, como (14) 99999-9999.';
        }
      }
      if (p.chave === 'altura') {
        const cm = alturaEmCm(v);
        if (cm == null || cm < 100 || cm > 230) {
          novosErros[chave] = 'Confira a altura em centímetros (ex.: 172).';
        }
      }
      if (p.chave === 'peso') {
        const kg = numeroBr(v);
        if (kg == null || kg < 25 || kg > 250) {
          novosErros[chave] = 'Confira o peso em kg (ex.: 62).';
        }
      }
      // Senha da fila: só o número (a tela já remove as letras).
      if (p.chave === 'senha_fila' && !normalizarSenha(v)) {
        novosErros[chave] = 'Digite o número da senha da fila.';
      }
    });

    // A pergunta de LGPD precisa ser "Sim" para concluir a inscrição.
    lista.forEach((p) => {
      if (p.chave !== 'lgpd' || p.tipo !== 'sim_nao') return;
      if (respostas[String(p.id)] !== 'true') {
        novosErros[String(p.id)] = 'Para concluir a inscrição é preciso aceitar.';
      }
    });

    return novosErros;
  }

  // Erro de senha repetida: mostra o texto do banco embaixo do campo da
  // senha, abre o bloco em que ele está e leva a pessoa até lá.
  function apontarErroDaSenha(perguntaId: number, mensagem: string) {
    setErros((prev) => ({ ...prev, [String(perguntaId)]: mensagem }));
    const idx = blocos.findIndex((b) => b.perguntas.some((q) => q.id === perguntaId));
    const trocaBloco = idx >= 0 && idx !== blocoAtual;
    if (trocaBloco) setBlocoAtual(idx);
    window.setTimeout(
      () => {
        const el = document.getElementById(`ficha-${perguntaId}`);
        if (!el) return;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        (el as HTMLInputElement).focus({ preventScroll: true });
      },
      trocaBloco ? 350 : 60,
    );
  }

  function scrollParaErro() {
    requestAnimationFrame(() => {
      const el = document.querySelector('[data-erro="true"]');
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Foca o primeiro campo da pergunta para abrir o teclado já no lugar certo.
      window.setTimeout(() => {
        const card = el.closest('div');
        const foco = card?.querySelector<HTMLElement>('input, select, textarea');
        if (foco && typeof foco.focus === 'function') foco.focus({ preventScroll: true });
      }, 300);
    });
  }

  function aoContinuar() {
    if (!blocos.length) return;
    const visiveis = visiveisPorBloco[blocoAtual] ?? [];
    const novosErros = validarPerguntas(visiveis);
    setErros(novosErros);
    if (Object.keys(novosErros).length > 0) {
      scrollParaErro();
      const qtd = Object.keys(novosErros).length;
      mostrarToast(qtd === 1 ? 'Falta preencher 1 campo ↑' : `Falta preencher ${qtd} campos ↑`);
      return;
    }
    const posicao = indicesVisiveis.indexOf(blocoAtual);
    const proximo = posicao >= 0 ? indicesVisiveis[posicao + 1] : indicesVisiveis[0];
    if (proximo != null && proximo !== blocoAtual) {
      setBlocoAtual(proximo);
    } else {
      void concluir();
    }
  }

  async function concluir() {
    if (!uid || !participanteId) return;

    // Se a foto ainda estiver subindo, espera no máximo 15 s.
    // Passou disso: conclui sem a foto, avisando na tela seguinte.
    setSalvando(true);
    await esperarFotos(15000);

    const novosErros = validarPerguntas(perguntasVisiveis);
    setErros(novosErros);
    if (Object.keys(novosErros).length > 0) {
      setSalvando(false);
      scrollParaErro();
      const qtd = Object.keys(novosErros).length;
      mostrarToast(qtd === 1 ? 'Falta preencher 1 campo ↑' : `Falta preencher ${qtd} campos ↑`);
      return;
    }

    setErroGeral(null);
    try {
      // 1) Cria a inscrição — ou reaproveita uma "cancelada" desta pessoa.
      let inscricaoId: number;
      let codigo: string;

      const { data: cancelada } = await supabase
        .from('inscricoes')
        .select('id, codigo')
        .eq('modalidade_id', Number(modalidadeId))
        .eq('participante_id', participanteId)
        .eq('status', 'cancelada')
        .limit(1)
        .maybeSingle();

      if (cancelada) {
        const { error: errReativa } = await supabase
          .from('inscricoes')
          .update({ status: 'pendente' })
          .eq('id', (cancelada as { id: number }).id);
        if (errReativa) throw errReativa;
        inscricaoId = (cancelada as { id: number }).id;
        codigo = (cancelada as { codigo: string }).codigo;
      } else {
        const { data: inscricao, error: errInscricao } = await supabase
          .from('inscricoes')
          .insert({
            evento_id: partes[0].evento_id,
            modalidade_id: Number(modalidadeId),
            opcao_id: grupoId ?? null,
            participante_id: participanteId,
            responsavel_id: uid,
          })
          .select('id, codigo')
          .single();
        if (errInscricao) throw errInscricao;
        inscricaoId = (inscricao as { id: number }).id;
        codigo = (inscricao as { codigo: string }).codigo;
      }

      // Foto da ficha: usa o último caminho enviado, mesmo que o estado do
      // React ainda não tenha atualizado (envio terminando agora).
      const perguntaFoto = perguntasVisiveis.find((p) => p.tipo === 'foto');
      const chaveFoto = perguntaFoto ? String(perguntaFoto.id) : null;
      const doRascunho = chaveFoto ? respostas[chaveFoto] : undefined;
      const caminhoFoto =
        (chaveFoto && caminhosFotoRef.current[chaveFoto]) ||
        (typeof doRascunho === 'string' ? doRascunho : null) ||
        fotoCadastro ||
        null;
      // Sem foto: a inscrição é salva do mesmo jeito e a tela seguinte avisa.
      const fotoFaltando = !!perguntaFoto && !(caminhoFoto && caminhoFoto.trim());

      // 3) Grava as respostas de uma vez.
      const linhas: { inscricao_id: number; pergunta_id: number; valor: unknown }[] = [];
      perguntasVisiveis.forEach((p) => {
        const chave = String(p.id);
        if (p.tipo === 'foto') {
          if (caminhoFoto && caminhoFoto.trim()) {
            linhas.push({ inscricao_id: inscricaoId, pergunta_id: p.id, valor: caminhoFoto });
          }
          return;
        }
        const v = respostas[chave];
        if (p.tipo === 'sim_nao') {
          if (v === 'true' || v === 'false') {
            linhas.push({ inscricao_id: inscricaoId, pergunta_id: p.id, valor: v === 'true' });
          }
          return;
        }
        if (p.tipo === 'lista') {
          const linhasLista = (Array.isArray(v) ? v : []) as LinhaLista[];
          const limpas = linhasLista.filter((l) =>
            Object.values(l).some((col) => String(col ?? '').trim() !== ''),
          );
          linhas.push({ inscricao_id: inscricaoId, pergunta_id: p.id, valor: limpas });
          return;
        }
        if (Array.isArray(v)) {
          if (v.length > 0) linhas.push({ inscricao_id: inscricaoId, pergunta_id: p.id, valor: v });
          return;
        }
        if (typeof v === 'string' && v.trim()) {
          let valor = v;
          // Altura sempre em centímetros e peso em kg, mesmo sem sair do campo.
          if (p.chave === 'altura') valor = normalizarAltura(v);
          if (p.chave === 'peso') valor = normalizarPeso(v);
          if (p.chave === 'senha_fila') valor = normalizarSenha(v);
          linhas.push({ inscricao_id: inscricaoId, pergunta_id: p.id, valor });
        }
      });

      if (linhas.length > 0) {
        const { error: errRespostas } = await supabase.from('respostas').insert(linhas);
        if (errRespostas) {
          // Inscrição sem ficha é pior que inscrição nenhuma: marca como cancelada.
          await supabase.from('inscricoes').update({ status: 'cancelada' }).eq('id', inscricaoId);

          // Senha já cadastrada no mesmo grupo: o banco manda o texto pronto.
          // Nada das outras respostas é perdido — a pessoa só troca o número.
          const msgSenha = mensagemSenhaRepetida(errRespostas);
          const perguntaSenha = perguntasVisiveis.find((p) => p.chave === 'senha_fila');
          if (msgSenha && perguntaSenha) {
            apontarErroDaSenha(perguntaSenha.id, msgSenha);
            return;
          }

          setErroGeral('Não conseguimos salvar sua ficha. Tente de novo.');
          return;
        }
      }

      // 3.1) Quem inscreveu a si mesmo e enviou foto já fica com ela no perfil
      // (é a mesma ficha: participantes.usuario_id = a pessoa). Best-effort.
      if (ehMinhaFicha && caminhoFoto && caminhoFoto !== fotoCadastro) {
        await supabase
          .from('participantes')
          .update({ foto_url: caminhoFoto })
          .eq('id', participanteId);
      }

      // 4) Limpa o rascunho e vai para a confirmação.
      try {
        localStorage.removeItem(chaveRascunho);
      } catch {
        // ignora
      }
      navigate(`/minha-inscricao/${codigo}?nova=1${fotoFaltando ? '&foto=0' : ''}`);
    } catch (e) {
      setErroGeral(mensagemAmigavel(e));
    } finally {
      setSalvando(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background-100">
        <div className="mx-auto w-full max-w-[720px] px-4 pb-28 pt-6">
          <div className="h-1.5 w-full animate-pulse rounded-full bg-secondary-200" />
          <div className="mt-4 h-16 w-full animate-pulse rounded-lg bg-secondary-100" />
          <div className="mt-4 h-40 w-full animate-pulse rounded-lg bg-secondary-100" />
          <div className="mt-4 h-64 w-full animate-pulse rounded-lg bg-secondary-100" />
        </div>
      </div>
    );
  }

  if (erro) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-100 px-4 py-8">
        <div className="w-full max-w-[420px] overflow-hidden rounded-lg border border-secondary-200 bg-white">
          <div className="h-[10px] w-full bg-primary-500" />
          <div className="p-6 text-center">
            <h1 className="text-xl font-normal text-foreground-950">Não foi possível continuar</h1>
            <p className="mt-2 text-sm text-secondary-500">{erro}</p>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="mt-6 w-full cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-primary-600"
            >
              Ver eventos disponíveis
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (blocos.length === 0) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-100 px-4 py-8">
        <div className="w-full max-w-[420px] overflow-hidden rounded-lg border border-secondary-200 bg-white">
          <div className="h-[10px] w-full bg-primary-500" />
          <div className="p-6 text-center">
            <p className="text-[15px] text-foreground-800">Esta ficha ainda não tem perguntas.</p>
          </div>
        </div>
      </div>
    );
  }

  const blocoAtualDados = blocos[blocoAtual] ?? blocos[0];
  const visiveisDoBloco = visiveisPorBloco[blocoAtual] ?? [];
  const ehUltimoBloco =
    posicaoNoBloco < 0 || posicaoNoBloco === indicesVisiveis.length - 1;

  return (
    <div className="min-h-screen bg-background-100">
      {/* Topo fixo */}
      <header className="fixed inset-x-0 top-0 z-20 border-b border-secondary-200 bg-white">
        <div className="mx-auto w-full max-w-[720px] px-4 py-3">
          <p className="truncate text-sm font-medium text-foreground-950">{eventoNome}</p>
          {subtitulo && (
            <p className="mt-0.5 truncate text-xs font-medium text-primary-600">{subtitulo}</p>
          )}
          {exibirBlocos && (
            <>
              <p className="mt-0.5 text-xs text-secondary-500">
                Bloco {posicaoNoBloco >= 0 ? posicaoNoBloco + 1 : 1} de{' '}
                {indicesVisiveis.length}
                {blocoAtualDados.secao ? ` · ${blocoAtualDados.secao}` : ''}
              </p>
              <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-secondary-200">
                <div
                  className="h-full rounded-full bg-primary-500 transition-all duration-300"
                  style={{ width: `${progresso}%` }}
                />
              </div>
            </>
          )}
        </div>
      </header>

      <main className="mx-auto w-full max-w-[720px] px-4 pb-28 pt-24">
        {rascunhoRecuperado && (
          <div className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-secondary-200 bg-white px-4 py-3">
            <p className="text-sm text-secondary-700">
              Recuperamos o que você já tinha respondido.
            </p>
            <button
              type="button"
              onClick={comecarDoZero}
              className="cursor-pointer whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
            >
              começar do zero
            </button>
          </div>
        )}

        {/* Descrição do formulário só no primeiro bloco */}
        {posicaoNoBloco === 0 && formDescricao && (
          <div className="mb-4 rounded-lg border border-secondary-200 bg-secondary-50 px-4 py-4">
            <TextoRico
              texto={formDescricao}
              className="text-sm text-foreground-800 text-justify"
            />
          </div>
        )}

        <div className="space-y-4">
          {visiveisDoBloco.map((p) => (
            <div key={p.id} className="rounded-lg border border-secondary-200 bg-white p-6">
              {/* Se este campo quebrar, só ele mostra o aviso — o resto segue. */}
              <ProtecaoPergunta>
                <div className="flex flex-wrap items-baseline gap-x-1 text-base font-medium text-foreground-950">
                  <TextoRico texto={p.enunciado} />
                  {p.obrigatoria && <span className="text-danger-600">*</span>}
                </div>
                {p.descricao && p.chave !== 'senha_fila' && (
                  <TextoRico texto={p.descricao} className="mt-1 text-sm text-secondary-500" />
                )}

                <div className="mt-3">
                  {p.tipo === 'foto' ? (
                    <CampoFoto
                      arquivo={fotos[String(p.id)] ?? null}
                      erro={erros[String(p.id)] || null}
                      enviando={enviandoFoto[String(p.id)] ?? false}
                      enviada={typeof respostas[String(p.id)] === 'string'}
                      fotoExistente={
                        (respostas[String(p.id)] as string | undefined) ?? fotoCadastro ?? null
                      }
                      aoSelecionar={(f) => aoSelecionarFoto(p.id, f)}
                      cameraTraseira={!ehMinhaFicha}
                    />
                  ) : (
                    <CampoPergunta
                      pergunta={p}
                      valor={respostas[String(p.id)]}
                      erro={erros[String(p.id)] || null}
                      onChange={(v) => aoResponder(p.id, v)}
                    />
                  )}
                </div>

                {avisoNaoQuer(p) && (
                  <p className="mt-2 rounded-md bg-amber-100 px-3 py-2 text-sm text-amber-800">
                    {avisoNaoQuer(p)}
                  </p>
                )}
              </ProtecaoPergunta>
            </div>
          ))}
        </div>

        {blocoAtualDados.tinhaTermo && levaFilhos && (
          <p className="mt-3 rounded-lg bg-secondary-50 px-4 py-3 text-[13px] leading-relaxed text-secondary-700">
            Os dados de saúde e a autorização das crianças são preenchidos pelo link que a direção
            vai mandar.
          </p>
        )}

        {erroGeral && (
          <div
            ref={refErro}
            className="mt-4 rounded-md border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700"
          >
            {erroGeral}
          </div>
        )}
      </main>

      {/* Rodapé fixo */}
      <footer className="fixed inset-x-0 bottom-0 z-20 border-t border-secondary-200 bg-white px-4 py-3">
        <div className="mx-auto w-full max-w-[720px]">
          {salvando && (
            <div className="mb-2">
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-secondary-200">
                <div className="h-full w-1/2 animate-pulse rounded-full bg-primary-500" />
              </div>
              <p className="mt-1 text-xs text-secondary-500">Enviando…</p>
            </div>
          )}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => {
                const posicao = indicesVisiveis.indexOf(blocoAtual);
                if (posicao <= 0) {
                  navigate(`/inscricao/${modalidadeId}${opcaoParam ? `?opcao=${opcaoParam}` : ''}`);
                } else {
                  setBlocoAtual(indicesVisiveis[posicao - 1]);
                }
              }}
              disabled={salvando}
              className="cursor-pointer whitespace-nowrap rounded-md px-4 py-3 text-[15px] font-medium text-secondary-600 transition-colors hover:bg-background-100 disabled:cursor-not-allowed"
            >
              Voltar
            </button>
            <button
              type="button"
              onClick={aoContinuar}
              disabled={salvando}
              className="flex h-12 flex-1 cursor-pointer items-center justify-center whitespace-nowrap rounded-md bg-primary-500 text-[15px] font-medium text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:bg-secondary-200 disabled:text-secondary-400"
            >
              {salvando ? 'Enviando…' : ehUltimoBloco ? 'Concluir inscrição' : 'Continuar'}
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}